import { useCallback, useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'

import CategoryRow from '../trips/CategoryRow.jsx'
import Estimate from '../trips/Estimate.jsx'
import TierBadge from '../components/TierBadge.jsx'
import { CATEGORIES } from '../trips/estimate.js'
import { comparable, splitPlaces } from '../trips/compare.js'
import { loadCompare } from '../trips/api.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// One place's board, read-only — P5-3 T9.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THIS IS "Open it, read-only", AND IT IS WHAT MAKES CHOOSING SAFE
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF: *"Losing destinations are archived, never deleted. Every entry,
// star, note and unfurled photo survives. Archive rows offer 'Open it,
// read-only' and 'Start next year's trip from this'."*
//
// Choosing is the one action on the compare screen that feels irreversible, and
// the thing that makes it not be is that the loser is still here, whole. This
// route is where "still here" becomes something a person can look at. Without
// it, the guarantee is a comment in a migration.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 READ-ONLY, AND IT SAYS SO RATHER THAN JUST OMITTING THE BUTTONS
// ═══════════════════════════════════════════════════════════════════════════
//
// A page with no add form and no vote buttons and no explanation reads as a
// page that is broken, or as one whose controls have not loaded yet. So the
// state is named at the top, in words, with the way back to the surface that
// does take writes.
//
// ⚠️ It is read-only for a LIVE place too, and that is a stated boundary rather
// than an oversight: the trip board is the write surface (it filters to the
// chosen place, per HANDOFF), and building a second, per-place write surface
// would be two boards to keep honest instead of one. Recorded in the PR body.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT READS THE COMPARE ENDPOINT, WHICH IS THE ONLY ONE THAT CARRIES ARCHIVES
// ═══════════════════════════════════════════════════════════════════════════
//
// `/api/trips/:id` — the board — asks the service for non-archived destinations
// and a non-archived estimate, deliberately, so that the board can never learn
// to render an archived place. An archived place therefore has exactly one read
// that can see it, and this is its consumer.
//
// ⚠️ NO LIVE CHANNEL HERE. An archived board does not change, and a live
// board's changes are the board's to show. Opening a second SSE connection for
// a page whose whole purpose is to be a record would be a transport spent on
// nothing — and §5.6's rule is about there being one mechanism, not about
// every page using it.

export default function TripPlace({ section }) {
  const { tripId, placeId } = useParams()
  const [state, setState] = useState({ status: 'loading', data: null, error: null })
  // Every row open: this is a record somebody came here to read, not a board
  // they are working through. The toggles still work — a collapsed row is a
  // reading choice, and removing the control would make the header look broken.
  const [openRows, setOpenRows] = useState({ stay: true, eat: true, do: true, travel: true })
  const toggleRow = useCallback((key) => {
    setOpenRows((rows) => ({ ...rows, [key]: !rows[key] }))
  }, [])

  useDocumentTitle(state.data?.trip?.title ?? section?.title ?? 'Trips')

  useEffect(() => {
    let cancelled = false
    loadCompare(tripId).then(
      (data) => {
        if (!cancelled) setState({ status: 'ready', data, error: null })
      },
      (error) => {
        if (!cancelled) setState({ status: 'error', data: null, error: error.message })
      },
    )
    return () => {
      cancelled = true
    }
  }, [tripId])

  if (state.status === 'loading') {
    return (
      <article className="d2d-page">
        <p className="d2d-notice" data-testid="place-loading">
          Loading.
        </p>
      </article>
    )
  }

  if (state.status === 'error') {
    return (
      <article className="d2d-page">
        <p className="d2d-notice" data-testid="place-error">
          {state.error}
        </p>
        <p>
          <Link to={`/trips/${tripId}`}>Back to the board</Link>
        </p>
      </article>
    )
  }

  const { trip, entries, estimate } = state.data
  const { all } = splitPlaces(state.data.destinations)
  const place = all.find((candidate) => candidate.id === placeId) ?? null

  // 🔴 THE SAME RULE THE COMPARE SCREEN OBEYS, FOR THE SAME REASON. A trip that
  // has never had two places has no place surface at all — HANDOFF: "the
  // concept is completely invisible." A URL that names one either belongs to a
  // trip that does not have it, or to a trip that never had places; both land
  // on the plain board rather than rendering a name.
  if (!place || !comparable(state.data.destinations)) {
    return <Navigate to={`/trips/${tripId}`} replace />
  }

  const roll = (Array.isArray(estimate?.by_destination) ? estimate.by_destination : []).find(
    (row) => row.destination_id === place.id,
  )
  const mine = entries.filter((entry) => !entry.removed && entry.destination_id === place.id)

  return (
    <article className="d2d-page d2d-board d2d-place">
      <header className="d2d-titleblock">
        <p className="d2d-eyebrow d2d-micro">
          <TierBadge tier={3} />
        </p>
        <p className="d2d-board__back">
          <Link to={`/trips/${tripId}/compare`}>← the comparison</Link>
        </p>
        <h1 className="d2d-titleblock__headline">{place.name}</h1>
        <p className="d2d-titleblock__lede" data-testid="place-readonly">
          {place.archived
            ? 'Archived, and kept whole. Everything anybody put here is still here — you can read it, and nothing on this page changes it.'
            : 'A read-only look at this one place. Adding and starring happen on the trip board.'}
        </p>
        <hr className="d2d-rule" />
      </header>

      {/* The same roll-up bar and the same three honesty rules as the board,
          over this place's entries only — the service computed it, per
          destination, and nothing here re-derives it. */}
      <Estimate estimate={{ overall: roll?.overall }} headcount={trip.headcount} />

      <div className="d2d-board__rows">
        {CATEGORIES.map((category) => (
          <CategoryRow
            key={category.key}
            category={category}
            entries={mine.filter((entry) => entry.category === category.key)}
            rollUp={roll?.by_category?.[category.key]}
            open={Boolean(openRows[category.key])}
            onToggle={toggleRow}
            you={state.data.you}
            readOnly
          />
        ))}
      </div>

      <p className="d2d-place__foot">
        <Link to={`/trips/${tripId}`}>Back to the board</Link>
      </p>
    </article>
  )
}
